/**
 * Rich text formatting utilities for adding featured/highlighted text markers
 */

/**
 * Options for formatRichText function
 */
export interface RichTextFormatOptions {
    /** The message/text to format (already translated) */
    message: string;
    /** Values to be highlighted/featured (wrapped with {{}}) */
    featured?: Record<string, string>;
    /** Values to be replaced normally (without highlighting) */
    normal?: Record<string, string>;
}

/**
 * Represents a parsed segment of rich text
 */
export interface RichTextSegment {
    /** The text content of the segment */
    text: string;
    /** Whether this segment should be featured/highlighted */
    featured: boolean;
}

/**
 * Formats a message by replacing placeholders with featured or normal text
 *
 * @param options - Configuration object with named parameters
 *
 * @example
 * // In locale file: "transfer_message": "Transfer {datasource} to user {user}"
 *
 * formatRichText({
 *   message: t("transfer_message"),
 *   featured: { datasource: "Mi Datasource" },  // Will be highlighted
 *   normal: { user: username }                   // Normal text
 * })
 * // Result: "Transfer {{Mi Datasource}} to user john"
 *
 * @example
 * // Multiple featured values with same placeholder appearing multiple times
 * formatRichText({
 *   message: t("delete_confirm"),
 *   featured: { datasource: "dataExample", id: "123" }
 * })
 */
export function formatRichText(options: RichTextFormatOptions): string {
    const { message, featured = {}, normal = {} } = options;

    let result = message;

    // Replace featured values (will be highlighted) - replaces ALL occurrences
    Object.entries(featured).forEach(([placeholder, value]) => {
        const regex = new RegExp(`\\{${placeholder}\\}`, "g");
        result = result.replace(regex, `{{${value}}}`);
    });

    // Replace normal values (won't be highlighted) - replaces ALL occurrences
    Object.entries(normal).forEach(([placeholder, value]) => {
        const regex = new RegExp(`\\{${placeholder}\\}`, "g");
        result = result.replace(regex, value);
    });

    return result;
}

/**
 * Simpler version - all placeholders become featured
 *
 * @example
 * formatAllFeatured({
 *   message: t("delete_datasource"),
 *   values: { name: "dataExample" }
 * })
 */
export function formatAllFeatured(options: { message: string; values: Record<string, string> }): string {
    return formatRichText({
        message: options.message,
        featured: options.values,
    });
}

/**
 * Parses a formatted rich text string into segments for rendering
 * Segments wrapped in {{}} are marked as featured/highlighted
 *
 * @param formattedText - A string with {{featured}} markers
 * @returns Array of segments with text and featured flag
 *
 * @example
 * const text = "Transfer {{Mi Datasource}} to user john";
 * const segments = parseRichText(text);
 * // [
 * //   { text: "Transfer ", featured: false },
 * //   { text: "Mi Datasource", featured: true },
 * //   { text: " to user john", featured: false }
 * // ]
 */
export function parseRichText(formattedText: string): RichTextSegment[] {
    if (!formattedText) return [];

    const segments: RichTextSegment[] = [];
    const regex = /\{\{([^}]+)\}\}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(formattedText)) !== null) {
        // Normal text before the highlighted segment
        if (match.index > lastIndex) {
            segments.push({
                text: formattedText.substring(lastIndex, match.index),
                featured: false,
            });
        }

        // Featured text segment
        segments.push({
            text: match[1],
            featured: true,
        });

        lastIndex = regex.lastIndex;
    }

    // Remaining normal text after the last match
    if (lastIndex < formattedText.length) {
        segments.push({
            text: formattedText.substring(lastIndex),
            featured: false,
        });
    }

    return segments;
}
